import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import classNames from 'classnames';
import { isEmpty, omit } from 'lodash';

import Dropdown from 'components/Dropdown';
import { isTabletOrMobileDevice } from 'constants/app';
import { MARKET_GRAPH_BASE_URL, MARKET_GRAPH_SELECTION_URL } from 'constants/locations';
import { MARKET_GRAPH_DROPDOWN_TAB_INDEX } from 'constants/marketGraph';
import useDevice from 'hooks/useDevice';
import { getMarketGraphIsSmallScreenView } from 'redux/modules/appConfigs/selectors';
import { getMarketDefinitionSelectionStatuses } from 'redux/modules/marketDefinitions/selectors';
import { checkSportsMarkets, setIsMarketDataLoaded } from 'redux/modules/marketGraph';
import { getActiveMarkets, getGraphMarketEntries } from 'redux/modules/marketGraph/selectors';
import { DropdownItem } from 'types';

import styles from './MarketGraphDropdown.module.scss';

export default function MarketGraphDropdown({ isFancy = false }: { isFancy: boolean }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { marketId, selectionId, handicap = 0 } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const activeMarkets = useSelector(getActiveMarkets);
  const entries = useSelector(getGraphMarketEntries);
  const isMobileTabletView = useSelector(getMarketGraphIsSmallScreenView);
  const selectionStatuses = useSelector(getMarketDefinitionSelectionStatuses(marketId || ''));

  const { isMobile } = useDevice();

  const data =
    entries?.map((entry, index) => {
      const nonRunner = selectionStatuses?.find(
        selectionStatus =>
          selectionStatus.status !== 'ACTIVE' &&
          +selectionStatus.id === +entry.selectionId &&
          +(selectionStatus.hc || 0) === +(entry.handicap || 0)
      );
      const isActive = isFancy ? activeMarkets[entry.marketId] || isEmpty(activeMarkets) : !nonRunner;
      const isSelected = isFancy
        ? marketId === entry.marketId
        : entry.selectionId.toString() === selectionId && +(entry.handicap || 0) === +handicap;

      return {
        label: entry.name,
        id: index,
        value: index.toString(),
        disabled: !isActive,
        tooltip: !isActive ? t('marketGraph.tooltips.selectionUnavailable') : '',
        selected: isSelected
      };
    }) || [];

  const dropdownItems = isFancy ? data.filter(item => !item.disabled) : data;
  const selectedItem = dropdownItems.find(item => item?.selected) || dropdownItems[0];
  const activeItems = dropdownItems.filter(item => !item?.disabled);
  const isDisabled = activeItems.length <= 1;

  const handleChangeDropdown = (item: DropdownItem) => {
    if (entries?.[+item.id]) {
      dispatch(setIsMarketDataLoaded(false));

      navigate(
        `${MARKET_GRAPH_BASE_URL}/${entries?.[+item.id].marketId}/${entries?.[+item.id].selectionId || 0}/${
          entries?.[+item.id].handicap || 0
        }${location.search}`,
        { replace: true }
      );
    }
  };

  const onOpen = (isOpen: boolean) => {
    if (isOpen && !isDisabled) {
      if (isTabletOrMobileDevice) {
        navigate(`${MARKET_GRAPH_SELECTION_URL}/${marketId}/${selectionId || 0}/${handicap || 0}${location.search}`, {
          replace: true
        });
      } else {
        const marketIds = entries?.map(entry => entry.marketId) || [];
        if (isFancy && marketIds.length) {
          dispatch(checkSportsMarkets({ marketIds: marketIds }));
        }
      }
    }
  };

  useEffect(() => {
    const marketIds = entries?.map(entry => entry.marketId) || [];
    if (isFancy && marketIds.length) {
      dispatch(checkSportsMarkets({ marketIds: marketIds }));
    }
  }, [entries]);

  if (dropdownItems.length === 0) {
    return null;
  }

  return (
    <Dropdown
      tabIndex={MARKET_GRAPH_DROPDOWN_TAB_INDEX}
      tooltipElement={isMobile ? 'item' : 'label'}
      data={dropdownItems}
      hasAutoScroll={!isMobile}
      defaultValue={omit(selectedItem, ['tooltip'])}
      onSelectOption={handleChangeDropdown}
      onOpen={onOpen}
      hasAnimation
      disabled={isDisabled}
      classes={{
        placeholder: classNames(styles.placeholder, { [styles.placeholder__mobile]: isMobile || isMobileTabletView }),
        dropdown: classNames(styles.dropdown, { [styles.placeholder__hidden]: isMobile }),
        option: styles.option,
        noOptions: styles.noOptions,
        disabledDropdown: styles.dropdown__disabled,
        disabledOption: styles.disabledOption,
        selectedOption: styles.selectedOption,
        activePlaceholder: styles.placeholder__active
      }}
    />
  );
}
